import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { usersAPI } from "../api";
import useAuthStore from "../store/auth";

const CURRENCIES = [
  { code:"EUR", label:"Euro (€)" },
  { code:"USD", label:"Dollar ($)" },
  { code:"MGA", label:"Ariary (Ar)" },
  { code:"XOF", label:"Franc CFA (XOF)" },
  { code:"GBP", label:"Livre (£)" },
  { code:"ZAR", label:"Rand (R)" },
  { code:"CHF", label:"Franc suisse (CHF)" },
  { code:"CAD", label:"Dollar canadien (CA$)" },
];

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const { user, fetchMe, logout, isPremium, trialDaysLeft, isTrialActive } = useAuthStore();
  const navigate = useNavigate();
  const isFamily = user?.account_type === "family";

  const [form, setForm] = useState({
    name:         user?.name         || "",
    language:     user?.language     || "fr",
    currency:     user?.currency     || "EUR",
    account_type: user?.account_type || "personal",
  });
  const [pwForm, setPwForm]       = useState({ current_password:"", new_password:"" });
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [saving, setSaving]       = useState(false);

  const inp = {
    width:"100%", background:"var(--surface2)", border:"1px solid var(--border)",
    borderRadius:10, padding:"12px 16px", color:"var(--text)", fontSize:15,
    outline:"none", boxSizing:"border-box",
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await usersAPI.updateProfile(form);
      await fetchMe();
      i18n.changeLanguage(form.language);
      localStorage.setItem("bf_language", form.language);
      toast.success(t("profile_updated"));
    } catch (err) {
      toast.error(err.response?.data?.error || t("error"));
    } finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password.length < 6) return toast.error(t("password_min"));
    try {
      await usersAPI.changePassword(pwForm);
      toast.success(t("password_updated"));
      setPwForm({ current_password:"", new_password:"" });
    } catch (err) {
      toast.error(err.response?.data?.error || t("error"));
    }
  };

  const handleAccountTypeChange = (val) => {
    if (isFamily && val === "personal") return; // pas de retour en arriere
    setForm({ ...form, account_type: val });
    if (val === "family") setShowUpgrade(true);
  };

  const trialDays = trialDaysLeft();

  return (
    <div style={{ maxWidth:660 }}>
      <h1 style={{ fontFamily:"Syne,sans-serif", fontSize:24, fontWeight:700, marginBottom:28 }}>{t("profile_title")}</h1>

      {/* Carte identite */}
      <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:16, padding:28, marginBottom:20 }}>
        <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:24, paddingBottom:20, borderBottom:"1px solid var(--border)" }}>
          <div style={{ width:64, height:64, borderRadius:"50%", background:"linear-gradient(135deg,var(--accent),var(--accent2))", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, color:"#fff", fontWeight:700, fontFamily:"Syne,sans-serif", flexShrink:0 }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontFamily:"Syne,sans-serif", fontSize:20, fontWeight:700 }}>{user?.name}</div>
            <div style={{ color:"var(--text2)", fontSize:14, marginBottom:8 }}>{user?.email}</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              <span style={{ padding:"3px 12px", borderRadius:20, fontSize:12, fontWeight:600, background:isPremium()?"#7c6af720":"var(--surface2)", color:isPremium()?"var(--accent)":"var(--text2)", border:"1px solid "+(isPremium()?"var(--accent)":"var(--border)") }}>
                {isPremium() ? "⭐ Premium" : isTrialActive() ? "🕐 Essai — "+trialDays+"j" : "❌ Essai expire"}
              </span>
              <span style={{ padding:"3px 12px", borderRadius:20, fontSize:12, fontWeight:600, background:"var(--surface2)", color:"var(--text2)", border:"1px solid var(--border)" }}>
                {user?.account_type === "family" ? "👨‍👩‍👧 Familial" : "👤 Personnel"}
              </span>
            </div>
          </div>
        </div>

        <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:16 }}>{t("update_profile")}</h3>

        <div style={{ marginBottom:14 }}>
          <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("full_name")}</label>
          <input value={form.name} onChange={e => setForm({...form, name:e.target.value})} style={inp} />
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:14 }}>
          <div>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("language")}</label>
            <select value={form.language} onChange={e => setForm({...form, language:e.target.value})} style={{ ...inp, padding:"11px 14px" }}>
              <option value="fr">Francais</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("currency")}</label>
            <select value={form.currency} onChange={e => setForm({...form, currency:e.target.value})} style={{ ...inp, padding:"11px 14px" }}>
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
        </div>

        {/* Type de compte — switch personnel -> famille uniquement */}
        <div style={{ marginBottom:20 }}>
          <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("account_type")}</label>
          {isFamily ? (
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, fontSize:14 }}>
              <span style={{ fontSize:20 }}>👨‍👩‍👧</span>
              <div>
                <div style={{ fontWeight:600 }}>Compte Familial</div>
                <div style={{ fontSize:12, color:"var(--text2)" }}>Un compte familial ne peut pas etre converti en personnel.</div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <button onClick={() => handleAccountTypeChange("personal")}
                  style={{ padding:16, border:"2px solid "+(form.account_type==="personal"?"var(--accent)":"var(--border)"), borderRadius:12, background:form.account_type==="personal"?"#7c6af710":"var(--surface2)", cursor:"pointer", textAlign:"left" }}>
                  <div style={{ fontSize:20, marginBottom:6 }}>👤</div>
                  <div style={{ fontWeight:600, fontSize:14, color:"var(--text)", marginBottom:4 }}>Personnel</div>
                  <div style={{ fontSize:12, color:"var(--text2)" }}>Gestion individuelle de vos finances</div>
                </button>
                <button onClick={() => handleAccountTypeChange("family")}
                  style={{ padding:16, border:"2px solid "+(form.account_type==="family"?"var(--accent)":"var(--border)"), borderRadius:12, background:form.account_type==="family"?"#7c6af710":"var(--surface2)", cursor:"pointer", textAlign:"left" }}>
                  <div style={{ fontSize:20, marginBottom:6 }}>👨‍👩‍👧</div>
                  <div style={{ fontWeight:600, fontSize:14, color:"var(--text)", marginBottom:4 }}>Familial</div>
                  <div style={{ fontSize:12, color:"var(--text2)" }}>Finances partagees avec votre famille</div>
                </button>
              </div>
              {form.account_type === "family" && !isFamily && (
                <div style={{ marginTop:10, padding:"10px 14px", background:"#6af7b820", border:"1px solid #6af7b840", borderRadius:10, fontSize:13, color:"var(--accent3)" }}>
                  ✓ Passer en Familial activera le menu Famille et vous permettra de creer un groupe.
                </div>
              )}
            </div>
          )}
        </div>

        <button onClick={saveProfile} disabled={saving}
          style={{ padding:"12px 24px", background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer", opacity:saving?0.7:1 }}>
          {saving ? t("loading") : t("save")}
        </button>
      </div>

      {/* Mot de passe */}
      <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:16, padding:28, marginBottom:20 }}>
        <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:16 }}>{t("change_password")}</h3>
        <form onSubmit={changePassword}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("current_password")}</label>
            <input type="password" value={pwForm.current_password} onChange={e => setPwForm({...pwForm, current_password:e.target.value})} style={inp} />
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("new_password")}</label>
            <input type="password" value={pwForm.new_password} onChange={e => setPwForm({...pwForm, new_password:e.target.value})} style={inp} />
          </div>
          <button type="submit" style={{ padding:"12px 24px", background:"var(--surface2)", color:"var(--text)", border:"1px solid var(--border)", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>
            {t("update_profile")}
          </button>
        </form>
      </div>

      {/* Abonnement */}
      <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:16, padding:28 }}>
        <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:16 }}>Abonnement</h3>
        {isPremium() ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ color:"var(--accent)", fontWeight:600, marginBottom:4 }}>⭐ Vous etes abonne Premium</div>
              {user?.subscription_ends_at && (
                <div style={{ fontSize:13, color:"var(--text2)" }}>
                  Valide jusqu au : {new Date(user.subscription_ends_at).toLocaleDateString("fr-FR")}
                </div>
              )}
            </div>
            <button onClick={() => navigate("/pricing")} style={{ padding:"10px 20px", background:"var(--surface2)", color:"var(--text)", border:"1px solid var(--border)", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:600, cursor:"pointer" }}>
              Gerer
            </button>
          </div>
        ) : isTrialActive() ? (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ fontWeight:600, marginBottom:4 }}>🕐 Essai gratuit en cours</div>
              <div style={{ fontSize:13, color:"var(--text2)" }}>{trialDays} jour{trialDays > 1 ? "s" : ""} restant{trialDays > 1 ? "s" : ""}</div>
            </div>
            <button onClick={() => navigate("/pricing")} style={{ padding:"10px 20px", background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>
              {t("upgrade")}
            </button>
          </div>
        ) : (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
            <div>
              <div style={{ color:"var(--danger)", fontWeight:600, marginBottom:4 }}>❌ Essai expire</div>
              <div style={{ fontSize:13, color:"var(--text2)" }}>Passez a Premium pour continuer a utiliser BudgetFlow.</div>
            </div>
            <button onClick={() => navigate("/pricing")} style={{ padding:"10px 20px", background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>
              {t("upgrade")}
            </button>
          </div>
        )}
      </div>

      {/* Modal info upgrade famille */}
      {showUpgrade && (
        <div style={{ position:"fixed", inset:0, background:"#00000080", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:20, padding:32, maxWidth:420, width:"100%" }}>
            <div style={{ fontSize:48, textAlign:"center", marginBottom:16 }}>👨‍👩‍👧</div>
            <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:12, textAlign:"center" }}>Passer en compte Familial</h3>
            <p style={{ color:"var(--text2)", fontSize:14, lineHeight:1.7, marginBottom:20, textAlign:"center" }}>
              En passant en compte Familial, vous aurez acces au menu Famille pour inviter des membres et gerer un budget commun.
            </p>
            <div style={{ background:"#f7c26a20", border:"1px solid #f7c26a40", borderRadius:10, padding:12, marginBottom:20, fontSize:13, color:"#c98000" }}>
              ⚠️ Cette action est irreversible — un compte familial ne peut pas redevenir personnel.
            </div>
            <div style={{ display:"flex", gap:12 }}>
              <button onClick={() => { setForm({...form, account_type:"personal"}); setShowUpgrade(false); }}
                style={{ flex:1, padding:12, background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, cursor:"pointer", color:"var(--text)" }}>
                Annuler
              </button>
              <button onClick={() => setShowUpgrade(false)}
                style={{ flex:1, padding:12, background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}